import { MutatePlayerForm } from '@/features/player/components/mutate-player';
import { PlayerProfileDoc } from '@/features/player/components/types';
import { PlayerLastActiveFilter } from '@/features/player/context/player-filters-context';
import { getCurrentEnvUrls } from '@/libs/config';
import type { Address } from '@/types';
import { getCountryName, resolveAddressLocation } from '@/utils/address-location';
import { toCoachGenderFormValue } from '@/utils/player-gender';
import { Timestamp } from 'firebase/firestore';
import uuid from 'react-native-uuid';

export const LAST_ACTIVE_OPTIONS: Array<{
  text: PlayerLastActiveFilter;
  seconds: number | undefined;
}> = [
  { text: '1_month', seconds: 2628288 },
  { text: '3_months', seconds: 7776000 },
  { text: '6_months', seconds: 15778463 },
  { text: '1_5_years', seconds: 47335389 },
  { text: 'show_all', seconds: undefined }, // iOS => treated as nil
];

export const convertCreatePlayerFormToPlayerDoc = (
  form: MutatePlayerForm,
  academyId: string,
): PlayerProfileDoc => {
  return {
    id: uuid.v4(),
    firstname: form.firstName,
    lastname: form.lastName,
    academy_id: academyId,
    height: form.playerHeight.replace('"', '') ? Number(form.playerHeight.replace('"', '')) : null,
    gender: form.gender,
    level: Number(form.level.replace('level_', '')),
    parent_first: form.parentFirstName,
    parent_last: form.parentLastName,
    phone: form.phoneNumber,
    alternate_phone: form.altPhoneNumber,
    email: form.email,
    address: {
      country: getCountryName(form.country),
      city: form.city,
      state: form.state,
      zipcode: form.zipCode,
    },
    notes: null,
    inactive: null,
    dominanthand: form.dominantHand === 'left',
    clubsneeded: form.needClubs === 'yes',
    parent_id: '',
    createdOn: null,
    progress: null,
    dob: form.birthDate ? Timestamp.fromDate(form.birthDate) : null,
  };
};

export const convertMutatePlayerFormToPlayerUpdate = (
  form: MutatePlayerForm,
  academyId: string,
  existingAddress?: Address,
): Partial<PlayerProfileDoc> => {
  return {
    firstname: form.firstName,
    lastname: form.lastName,
    academy_id: academyId,
    height: form.playerHeight.replace('"', '') ? Number(form.playerHeight.replace('"', '')) : null,
    gender: form.gender,
    level: Number(form.level.replace('level_', '')),
    parent_first: form.parentFirstName,
    parent_last: form.parentLastName,
    phone: form.phoneNumber,
    alternate_phone: form.altPhoneNumber,
    email: form.email,
    address: {
      street1: existingAddress?.street1 ?? null,
      street2: existingAddress?.street2 ?? null,
      country: getCountryName(form.country),
      city: form.city,
      state: form.state,
      zipcode: form.zipCode,
    },
    dominanthand: form.dominantHand === 'left',
    clubsneeded: form.needClubs === 'yes',
    dob: form.birthDate ? Timestamp.fromDate(form.birthDate) : null,
  };
};

function playerHeightFromDoc(height: PlayerProfileDoc['height']): string {
  if (height == null || height === undefined) return '';
  if (typeof height === 'number') {
    if (!Number.isFinite(height)) return '';
    return `${height}"`;
  }
  const s = String(height).trim();
  if (!s) return '';
  if (s.endsWith('"')) return s;
  if (/^\d+$/.test(s)) return `${s}"`;
  return s;
}

export const convertPlayerDocToMutatePlayerForm = (player: PlayerProfileDoc): MutatePlayerForm => {
  const dob = player.dob?.seconds ? new Date(player.dob.seconds * 1000) : null;
  const levelNumber =
    typeof player.level === 'string' ? Number(player.level) : (player.level as number | null);
  const level = Number.isFinite(levelNumber) && levelNumber ? levelNumber : 1;

  const baseUrl = getCurrentEnvUrls().pictureUrl?.trim() || undefined;
  const inferredAvatarUri = baseUrl ? `${baseUrl}${player.id}.jpg?alt=media` : undefined;
  const existingAvatarUri =
    player.avatar_url ?? player.photo_url ?? player.image_url ?? inferredAvatarUri ?? undefined;
  const location = resolveAddressLocation(player.address?.country, player.address?.state);

  return {
    firstName: (player.firstname ?? '').toString(),
    lastName: (player.lastname ?? '').toString(),
    playerHeight: playerHeightFromDoc(player.height),
    gender: toCoachGenderFormValue(player.gender),
    level: `level_${level}`,
    parentFirstName: (player.parent_first ?? '').toString(),
    parentLastName: (player.parent_last ?? '').toString(),
    phoneNumber: (player.phone ?? '+1').toString(),
    altPhoneNumber: (player.alternate_phone ?? '').toString(),
    email: (player.email ?? '').toString(),
    country: location.country,
    city: (player.address?.city ?? '').toString(),
    state: location.state,
    zipCode: (player.address?.zipcode ?? '').toString(),
    birthDate: dob,
    needClubs: player.clubsneeded ? 'yes' : 'no',
    dominantHand: player.dominanthand ? 'left' : 'right',
    avatar: existingAvatarUri ? { uri: existingAvatarUri } : undefined,
  };
};

export function lastActiveSecondsByIndex(
  selectedActive: PlayerLastActiveFilter,
): number | undefined {
  const opt = LAST_ACTIVE_OPTIONS.find(o => o.text === selectedActive);
  return opt?.seconds;
}
